/**
 * Hypothesis entity — user-driven question or claim about case data.
 *
 * A Hypothesis carries:
 *  • title — required short label shown in lists, breadcrumbs, detail header
 *  • caseDescription — required plain-language statement of what the user is
 *    trying to prove (the longer grounding context)
 *  • items — 0..3 `{id, kind, text}` entries (kind ∈ {question, claim})
 *  • focusEntityIds — optional list of entity IDs to narrow the agent
 *  • status — Draft → Running → Awaiting review → Closed
 *  • verdict — Supported / Refuted / Inconclusive (set by the human)
 *  • runs (1..N) — autonomous agent runs, each producing a structured output
 *
 * PARTNER FILE — `src/libs/services/spectre/services/hypotheses.py`. The
 * Pydantic definitions there must stay structurally identical to these
 * types. Any change here MUST land in the same review as the matching
 * change there; otherwise producer drift breaks consumers at runtime.
 */

import type { Investigation } from './investigation'

export type HypothesisStatus = 'draft' | 'running' | 'awaiting_review' | 'closed'

export type HypothesisVerdict = 'supported' | 'refuted' | 'inconclusive'

export type HypothesisItemKind = 'question' | 'claim'

export type HypothesisRunStatus =
  | 'queued'
  | 'running'
  | 'finished_success'
  | 'finished_partial'
  | 'finished_error'

export const MAX_HYPOTHESIS_ITEMS = 3

export interface HypothesisItem {
  id: string
  kind: HypothesisItemKind
  text: string
}

export interface Hypothesis {
  id: string
  caseId: string
  title: string
  caseDescription: string
  items: HypothesisItem[]
  focusEntityIds: string[]
  status: HypothesisStatus
  verdict: HypothesisVerdict | null
  verdictText: string | null
  authorId?: string
  authorName: string
  /** ISO-8601 UTC */
  createdAt: string
  /** ISO-8601 UTC */
  updatedAt: string
  runCount: number
  triageViewCount: number
}

/** Body for POST /v1/cases/:caseId/hypotheses. */
export interface HypothesisCreateBody {
  title: string
  caseDescription: string
  items?: HypothesisItem[]
  focusEntityIds?: string[]
  authorId?: string
  authorName?: string
}

/** Body for PATCH /v1/cases/:caseId/hypotheses/:id (any subset). */
export interface HypothesisPatchBody {
  title?: string
  caseDescription?: string
  items?: HypothesisItem[]
  focusEntityIds?: string[]
  verdict?: HypothesisVerdict
  verdictText?: string
}

export interface HypothesisRun {
  id: string
  hypothesisId: string
  /** LangGraph checkpointer thread id — drives the runtime, not useful
   * to the frontend. Use `agentThreadUuid` for navigation. */
  agentThreadId: string | null
  /** AgentThread row's primary key (UUID). Use to navigate to the chat
   * page: `/cases/:caseId/agent?thread=<uuid>`. */
  agentThreadUuid: string | null
  status: HypothesisRunStatus
  summary: string | null
  /** Research output contract v2: full markdown report. */
  reportMd: string | null
  /** Citations backing the report: [{text, source: "node:<id>" | "doc:<id>"}]. */
  sources: Array<{ text: string; source: string }> | null
  /** Legacy verdict fields — populated only on pre-v2 runs. */
  verdictSuggestion: HypothesisVerdict | null
  confidence: number | null
  stepsCompleted: number
  stepsTotal: number | null
  errorMessage: string | null
  /** Free-form structured payload emitted by the agent on completion. */
  structuredOutput: Record<string, unknown> | null
  /** ISO-8601 UTC */
  startedAt: string
  /** ISO-8601 UTC */
  finishedAt: string | null
  /** The investigation this run's report was saved to (*Uložiť report*, ADR-0097). */
  reportInvestigationId?: string | null
}

/** A source whose reference named nothing in the case when the report was saved;
 *  it stays in the report as text only. `index` is its `[S#]` number. */
export interface DroppedResearchSource {
  index: number
  source: string
}

/** Answer of POST …/runs/:runId/report (*Uložiť report*, PRD-0076). Partner of
 *  `routers/v1/schemas/hypotheses.py` `SavedResearchReportOut`. */
export interface SavedResearchReport {
  investigation: Investigation
  droppedSources: DroppedResearchSource[]
  /** False when the save refreshed the investigation an earlier save created. */
  created: boolean
}

/** True for any terminal run status (finished_success / _partial / _error). */
export function isRunFinished(status: HypothesisRunStatus): boolean {
  return status.startsWith('finished')
}
